import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from "react";

import { TourMap, type TourStop } from "../TourMap";
import { fetchTourDates } from "../tourData";
import { getMapsUrl } from "../tourLinks";

function getVanListIndex(stops: TourStop[]) {
  if (stops.some((stop) => stop.dateStatus === "current")) {
    return null;
  }

  const previousStopIndex = stops.findLastIndex((stop) => stop.dateStatus === "past");
  const nextStopIndex = stops.findIndex((stop) => stop.dateStatus === "future");

  if (previousStopIndex === -1 || nextStopIndex === -1) {
    return null;
  }

  return nextStopIndex;
}

function VanIcon() {
  return (
    <svg viewBox="0 0 64 40" aria-hidden="true" focusable="false">
      <path d="M8 15h28l8 8h12v9H8z" />
      <path d="M39 17h7l5 5H44z" />
      <circle cx="20" cy="32" r="5" />
      <circle cx="48" cy="32" r="5" />
    </svg>
  );
}

function scrollListToElement(container: HTMLElement | null, target: HTMLElement | null) {
  if (!container || !target) {
    return;
  }

  const targetTop = target.offsetTop - container.offsetTop;
  const centeredTop = targetTop - container.clientHeight / 2 + target.clientHeight / 2;

  container.scrollTo({
    top: Math.max(0, centeredTop),
    behavior: "smooth",
  });
}

export function TourSection() {
  const [tourDates, setTourDates] = useState<TourStop[]>([]);
  const [tourStatus, setTourStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [selectedStopIndex, setSelectedStopIndex] = useState<number | null>(null);
  const [selectedVan, setSelectedVan] = useState(false);
  const tourListRef = useRef<HTMLDivElement | null>(null);
  const tourStopRefs = useRef<Array<HTMLElement | null>>([]);
  const vanListRef = useRef<HTMLButtonElement | null>(null);
  const vanListIndex = getVanListIndex(tourDates);
  const currentStopIndex = tourDates.findIndex((stop) => stop.dateStatus === "current");

  const selectStop = (index: number) => {
    setSelectedVan(false);
    setSelectedStopIndex(index);
  };

  const selectVan = () => {
    setSelectedStopIndex(null);
    setSelectedVan(true);
  };

  const handleStopKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectStop(index);
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchTourDates()
      .then((stops) => {
        if (!isMounted) {
          return;
        }

        setTourDates(stops);
        setTourStatus("ready");
        setSelectedStopIndex(null);
        setSelectedVan(false);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setTourDates([]);
        setTourStatus("failed");
        setSelectedStopIndex(null);
        setSelectedVan(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedVan) {
      scrollListToElement(tourListRef.current, vanListRef.current);
      return;
    }

    if (selectedStopIndex === null) {
      return;
    }

    scrollListToElement(tourListRef.current, tourStopRefs.current[selectedStopIndex] ?? null);
  }, [selectedStopIndex, selectedVan]);

  useEffect(() => {
    if (tourStatus === "loading") {
      return;
    }

    const target =
      currentStopIndex >= 0 ? tourStopRefs.current[currentStopIndex] : vanListRef.current;

    scrollListToElement(tourListRef.current, target ?? null);
  }, [currentStopIndex, tourStatus, vanListIndex]);

  if (tourStatus === "failed") {
    return (
      <section className="section tour-section" id="tournee">
        <div className="section-heading">
          <p className="kicker">Carte de la tournée</p>
          <h2>Dates à venir</h2>
        </div>
      </section>
    );
  }

  return (
    <section className="section tour-section" id="tournee">
      <div className="section-heading">
        <p className="kicker">Sur les routes en 2027</p>
      </div>
      <div className="tour-layout">
        <TourMap
          onSelectStop={selectStop}
          onSelectVan={selectVan}
          selectedStopIndex={selectedStopIndex}
          selectedVan={selectedVan}
          stops={tourDates}
        />
        <div className="tour-list" ref={tourListRef}>
          {tourDates.map((stop, index) => (
            <Fragment key={`${stop.city}-${stop.venue}-${stop.date}-${index}`}>
              {vanListIndex === index && (
                <button
                  className={`tour-list-van ${selectedVan ? "tour-list-van-selected" : ""}`}
                  aria-label="Le van est en route"
                  onClick={selectVan}
                  ref={vanListRef}
                  type="button"
                >
                  <VanIcon />
                  <div>
                    <h3>En route</h3>
                    <p>Vers la prochaine ville</p>
                  </div>
                  <time>Maintenant</time>
                </button>
              )}
              <div
                className={[
                  "tour-stop",
                  stop.dateStatus === "past" ? "tour-stop-past" : "",
                  stop.dateStatus === "current" ? "tour-stop-current" : "",
                  stop.dateStatus === "future" || !stop.dateStatus ? "tour-stop-future" : "",
                  selectedStopIndex === index ? "tour-stop-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => selectStop(index)}
                onKeyDown={(event) => handleStopKeyDown(event, index)}
                ref={(element) => {
                  tourStopRefs.current[index] = element;
                }}
                role="button"
                tabIndex={0}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>
                    {stop.coordinates ? (
                      <a
                        className="tour-stop-link"
                        href={getMapsUrl(stop.coordinates)}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {stop.city}
                      </a>
                    ) : (
                      stop.city
                    )}
                    {stop.dateStatus === "current" && (
                      <span className="tour-list-star" aria-label="Ville du jour">
                        ★
                      </span>
                    )}
                  </h3>
                  <p>
                    {stop.venueUrl ? (
                      <a
                        className="tour-stop-link"
                        href={stop.venueUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {stop.venue}
                      </a>
                    ) : (
                      stop.venue
                    )}
                  </p>
                </div>
                <time
                  className="tour-stop-schedule"
                  dateTime={stop.scheduleDateTime}
                >
                  <span className="tour-stop-date">{stop.date}</span>
                  {stop.time ? <span className="tour-stop-time">{stop.time}</span> : null}
                </time>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
